import axios from "axios";
import environment from "../environments/environment";
import {
  clearAuthAndRedirect,
  isAccessTokenExpiredMessage,
  isSessionInvalidMessage,
  refreshAuthSession,
} from "../utils/authRefresh";
import { getDashboardContextValue } from "../utils/dashboardContext";

const api = axios.create({
  baseURL: environment.apiUrl,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  },
});

api.interceptors.request.use(
  (config) => {
    const currentAuth = config.headers?.Authorization || config.headers?.authorization;
    if (currentAuth && (String(currentAuth).includes("undefined") || String(currentAuth).includes("null"))) {
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }
    const eventSlug = getDashboardContextValue("eventSlug");
    if (eventSlug) {
      config.headers["X-Event-Slug"] = eventSlug;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const errorMessage = error.response?.data?.errorMessage || error.response?.data?.message || "";
    const originalRequest = error.config;
    const temporaryFailure = !error.response || [408, 500, 502, 503, 504].includes(error.response.status);
    // Retry only opted-in reads; never replay writes or refresh-token rotation.
    if (
      originalRequest?.retryOnTemporaryError &&
      String(originalRequest.method || "get").toLowerCase() === "get" &&
      temporaryFailure && !axios.isCancel(error) &&
      (originalRequest._connectionRetries || 0) < 2
    ) {
      originalRequest._connectionRetries = (originalRequest._connectionRetries || 0) + 1;
      await new Promise((resolve) => setTimeout(resolve, originalRequest._connectionRetries * 500));
      return api(originalRequest);
    }

    if (
      isAccessTokenExpiredMessage(errorMessage) &&
      originalRequest &&
      !originalRequest._authRetry &&
      !String(originalRequest.url || "").includes("/auth/refresh")
    ) {
      originalRequest._authRetry = true;
      const refreshed = await refreshAuthSession();
      if (refreshed) {
        return api(originalRequest);
      }
      clearAuthAndRedirect();
    } else if (isSessionInvalidMessage(errorMessage)) {
      clearAuthAndRedirect();
    } else if (error.response?.status === 401) {
      console.warn("Unauthorized — maybe redirect to login");
    }
    return Promise.reject(error);
  },
);

export default api;
